import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import Grid from '../../webcommon/grid/Grid';
import ActionButton from '../../webcommon/common/actionbutton/ActionButton';
import moment from 'moment';
import styles from './styles/PricingParameters.module.scss';
import GridHeader from '../../common/gridheader/GridHeader';
import NewRow from '../../common/gridheader/newrow/NewRow';
import Templates from '../../common/gridheader/templates/Templates';
import ExportAsCsvButton from '../../common/gridheader/exportbutton/ExportAsCsvButton';
import {
    combinedColumnsSelector, getLoading, getRows, getRowMappings, getTemplates, getTrades,
    getTemplateSaved, getTemplateInProcessOfSaving, getRowsAndErrors
} from './selectors';
import ToggleSwitch from '../../common/gridheader/toggleswitch/ToggleSwitch';
import gridStyles from './styles/GridOverride.module.scss';
import actionButtonStyles from './styles/ActionButtonOverride.module.scss';
import {
    addPricingParametersRow,
    csvExportEmpty, deletePricingParametersRow, removeAllRows,
    pricingParametersLoaded, setActiveValue, deleteTemplate,
    selectTemplate, saveAsTemplate, submitPricingParameters, submitPricingParametersFailed,
    deriveAmountUsd, getDefaultBucket, setTemplateSaved, uploadTemplateError,
    saveAsTemplateFailed, loadSelectedRows, importSensitivities, copyTradeId, copyTradeIdFailed, togglePricingParameters, removeTogglePricingParameters
} from './actions';
import * as appConstants from '../../common/constants';

const calculateCsvFilename = date => `Pricing_Parameters_${(date ? moment(date) : moment()).format(appConstants.dateFormat)}.csv`;

const processDataForCsvExport = (rows) => {
    const mapKeys = (row) => {
        const {
            tradeId, imModel, prodClass, riskType, qualifier, labelOne, labelTwo,
            bucket, amountCcy, amount, amountUsd, date
        } = row;
        return {
            'Trade ID': tradeId,
            'IM Model': imModel,
            'Prod Class': prodClass,
            'Risk Type': riskType,
            Qualifier: qualifier,
            'Label 1': labelOne,
            'Label 2': labelTwo,
            Bucket: bucket,
            EndDate: date ? moment(date).format(appConstants.dateFormat) : '',
            'Amount Ccy': amountCcy,
            Amount: amount,
            'Amount USD': amountUsd
        };
    };
    return rows.map(mapKeys);
};

const emptyTradeIds = trades => trades.filter(row => row.tradeId.length === 0).length;

const alreadyExistingTrades = (tradesToSubmit, existingTrades) => tradesToSubmit
    .filter(trade => existingTrades.find(t => t.name === trade.tradeId))
    .map(t => t.tradeId);

const processDataForCsvImport = (rows) => {
    const mapKeys = row => ({
        tradeId: row['Trade ID'],
        imModel: row['IM Model'],
        prodClass: row['Prod Class'],
        riskType: row['Risk Type'],
        qualifier: row.Qualifier,
        labelOne: row['Label 1'],
        labelTwo: row['Label 2'],
        bucket: row.Bucket,
        date: row.EndDate,
        amountCcy: row['Amount Ccy'],
        amount: row.Amount,
        amountUsd: row['Amount USD']
    });
    return rows.map(mapKeys);
};

const anErrorExistsInGrid = (gridErrors) => {
    const errorValues = gridErrors.map(errorObject => Object.keys(errorObject).map(
        key => errorObject[key] && errorObject[key].error
    ));
    const includesAnError = errorValues.map(x => x.includes(true));
    const errorExists = includesAnError.find(x => x === true);
    return errorExists || false;
};

const PricingParameters = (props) => {
    const {
        pricingParametersLoaded,
        readOnly,
        uploadTemplateError,
        rows,
        copyTradeId,
        copyTradeIdFailed
    } = props;

    useEffect(() => {
        pricingParametersLoaded();
    }, [readOnly, pricingParametersLoaded]);

    const [selectedImModel, setSelectedImModel] = useState('SIMM');

    const toggleImModel = (e) => {
        selectedImModel === 'SIMM' ? setSelectedImModel('STD') : setSelectedImModel('SIMM');
        props.togglePricingParameters(selectedImModel === 'SIMM' ? 'STD' : 'SIMM')
    
        }

    const [dateRowBeingFocused, setDateRowBeingFocused] = useState(null);

    const setRowFocus = (row, focused) => (focused
        ? setDateRowBeingFocused(row) : setDateRowBeingFocused(null));

    const saveAsTemplate = (name) => {
        props.saveAsTemplate(name, props.rows);
    };

    const submitPricingParametersRows = () => {
        const existingTrades = alreadyExistingTrades(props.rows, props.trades);
        if (emptyTradeIds(props.rows)) {
            props.submitPricingParametersFailed('Cannot submit trades with an empty trade ID');
        } else if (existingTrades.length) {
            props.submitPricingParametersFailed(`The following trades have already been made with that ID: ${existingTrades.join(', ')}`);
        } else {
            props.submitPricingParameters(props.rows);
        }
    };

    const uploadError = useCallback(() => {
        uploadTemplateError('There was an error uploading the template');
    }, [uploadTemplateError]);

    const uploadAbort = useCallback(() => {
        uploadTemplateError('The template upload operation was aborted');
    }, [uploadTemplateError]);

    const copyTradeIds = useCallback(() => {
        if (rows && rows.length > 0) {
            copyTradeId();
        } else {
            copyTradeIdFailed('Copying the Trade ID failed. There must be at least one row.');
        }
    }, [rows, copyTradeId, copyTradeIdFailed]);

    return (
        <div className={ styles.impactPricingParameters }>
            { props.readOnly
                ? (
                    <GridHeader title="Pricing Parameters">
                        <ExportAsCsvButton
                            csvData={ props.rows }
                            csvExportEmpty={ props.csvExportEmpty }
                            csvFilename={ calculateCsvFilename(props.eodDate) }
                            csvMapping={ processDataForCsvExport }
                        />
                    </GridHeader>
                )
                : (
                    <GridHeader title="Pricing Parameters">
                        <div className={ styles.toggleSwitch }>
                            <ToggleSwitch
                                        optionLeft="STD"
                                        optionRight="SIMM"
                                        toggleIsLeft={ selectedImModel=== 'SIMM' }
                                        toggleSwitch={ toggleImModel }
                                    />
                        </div>
                        <div className={ styles.separator }>&nbsp;</div>
                        <Templates
                            deleteTemplate={ props.deleteTemplate }
                            importSensitivities={ props.importSensitivities }
                            rows={ props.templateRows }
                            saveAsTemplate={ saveAsTemplate }
                            saveAsTemplateFailed={ props.saveAsTemplateFailed }
                            selectTemplate={ props.selectTemplate }
                            setTemplateSaved={ props.setTemplateSaved }
                            templateInProcessOfSaving={ props.templateInProcessOfSaving }
                            templateSaved={ props.templateSaved }
                            uploadMapping={ processDataForCsvImport }
                            uploadTemplate={ props.loadSelectedRows }
                            uploadTemplateAbort={ uploadAbort }
                            uploadTemplateError={ uploadError }
                        />
                        <div className={ styles.separator }>&nbsp;</div>
                        <NewRow addRow={ props.addPricingParametersRow } />
                        <div className={ styles.separator }>&nbsp;</div>
                        <ExportAsCsvButton
                            csvData={ props.rows }
                            csvExportEmpty={ props.csvExportEmpty }
                            csvFilename={ calculateCsvFilename(props.eodDate) }
                            csvMapping={ processDataForCsvExport }
                        />
                    </GridHeader>
                ) }
            <div className={ styles.gridContainer }>
                {props.columns.length && (
                    <Grid
                        columns={ props.columns }
                        copyTradesIds={ copyTradeIds }
                        dateRowBeingFocused={ dateRowBeingFocused }
                        gridErrorsAndOptions={ props.gridErrorsAndOptions }
                        setDateRowBeingFocused={ setRowFocus }
                        defaultColumnSpacing={ 2 }
                        deleteRow={ props.deletePricingParametersRow }
                        deriveAmountUsd={ props.deriveAmountUsd }
                        getDefaultBucket={ props.getDefaultBucket }
                        loading={ props.loading }
                        rowMappings={ props.rowMappings }
                        rows={ props.rows }
                        setActiveValue={ props.setActiveValue }
                        styles={ gridStyles }
                    >
                        Pricing Params
                    </Grid>
                )}
            </div>

            <div className={ styles.pricingParameterGridButtons }>
                <div className={ styles.pricingParameterRemoveAllButton }>
                    <ActionButton
                        accent
                        onClick={ () => {props.removeAllRows();} }
                    >
                        Remove All
                    </ActionButton>
                </div>
                { !props.readOnly && (
                    <>
                        <div className={ styles.pricingParameterGridSubmitButton }>
                            <ActionButton
                                disabled={ anErrorExistsInGrid(props.gridErrorsAndOptions) }
                                invertedAccent
                                onClick={ () => {submitPricingParametersRows(); props.removeTogglePricingParameters()} }
                                styles={ actionButtonStyles }
                            >
                                Submit
                            </ActionButton>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

PricingParameters.propTypes = {
    addPricingParametersRow: PropTypes.func.isRequired,
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string,
            id: PropTypes.string
        })
    ),
    copyTradeId: PropTypes.func.isRequired,
    copyTradeIdFailed: PropTypes.func.isRequired,
    csvExportEmpty: PropTypes.func.isRequired,
    deletePricingParametersRow: PropTypes.func.isRequired,
    deleteTemplate: PropTypes.func.isRequired,
    deriveAmountUsd: PropTypes.func.isRequired,
    getDefaultBucket: PropTypes.func.isRequired,
    gridErrorsAndOptions: PropTypes.arrayOf(
        PropTypes.shape({ })
    ),
    rowMappings: PropTypes.shape({}),
    eodDate: PropTypes.string,
    loading: PropTypes.bool,
    loadSelectedRows: PropTypes.func.isRequired,
    importSensitivities: PropTypes.func.isRequired,
    pricingParametersLoaded: PropTypes.func.isRequired,
    submitPricingParameters: PropTypes.func.isRequired,
    submitPricingParametersFailed: PropTypes.func.isRequired,
    removeAllRows: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    saveAsTemplate: PropTypes.func.isRequired,
    saveAsTemplateFailed: PropTypes.func.isRequired,
    setActiveValue: PropTypes.func.isRequired,
    selectTemplate: PropTypes.func.isRequired,
    trades: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
            checked: PropTypes.bool,
            booked: PropTypes.bool
        })
    ),
    setTemplateSaved: PropTypes.func.isRequired,
    templateRows: PropTypes.arrayOf(PropTypes.string),
    templateInProcessOfSaving: PropTypes.bool,
    templateSaved: PropTypes.bool,
    uploadTemplateError: PropTypes.func.isRequired,
    readOnly: PropTypes.bool
};

PricingParameters.defaultProps = {
    columns: [],
    eodDate: '',
    gridErrorsAndOptions: [],
    loading: false,
    rowMappings: {},
    rows: [],
    templateRows: [],
    trades: [],
    templateInProcessOfSaving: false,
    templateSaved: false,
    readOnly: false
};

const mapStateToProps = state => ({
    columns: combinedColumnsSelector(state),
    gridErrorsAndOptions: getRowsAndErrors(state),
    loading: getLoading(state),
    rowMappings: getRowMappings(state),
    rows: getRows(state),
    templateRows: getTemplates(state),
    trades: getTrades(state),
    templateInProcessOfSaving: getTemplateInProcessOfSaving(state),
    templateSaved: getTemplateSaved(state)

});

const mapDispatchToProps = {
    addPricingParametersRow,
    copyTradeId,
    copyTradeIdFailed,
    csvExportEmpty,
    deletePricingParametersRow,
    deleteTemplate,
    deriveAmountUsd,
    getDefaultBucket,
    loadSelectedRows,
    importSensitivities,
    togglePricingParameters,
    removeTogglePricingParameters,
    saveAsTemplate,
    selectTemplate,
    setActiveValue,
    pricingParametersLoaded,
    saveAsTemplateFailed,
    setTemplateSaved,
    submitPricingParameters,
    submitPricingParametersFailed,
    removeAllRows,
    uploadTemplateError
};

export default withRouter(connect(mapStateToProps,
    mapDispatchToProps)(PricingParameters));

export { PricingParameters as PricingParametersUnconnected };
